/**
 * DegradedModeBadge — Story E1.5.3 (UX-DR15).
 *
 * Sinaliza visualmente, próximo ao sino de notificações, quando o
 * transporte real-time caiu do WebSocket para o SSE fallback
 * (Story E1.5.2). Permite que o usuário entenda que pode haver pequeno
 * atraso (~30s vs <5s) sem assumir que está tudo OK.
 *
 * Subscribe ao `realtime-store` (módulo-level, `useSyncExternalStore`) —
 * **não** monta nenhuma conexão própria, é puramente reativo.
 *
 * Trade-offs
 * ----------
 *  - Só renderiza para `mode === 'sse'`. Estados `connecting`/`disconnected`
 *    são transientes (≤3s) ou explícitos do consumidor — não merecem
 *    poluir visualmente o header.
 *  - Variante visual feita inline com Tailwind yellow porque o `Badge`
 *    primitivo do projeto só expõe `default|secondary|destructive|outline`.
 *    Decisão isolada à story XS — caso o design-system ganhe `warning` no
 *    futuro, trocar a uma linha.
 *  - Tooltip via Radix exige `TooltipProvider` ancestral (Header já tem
 *    indiretamente via shadcn root provider). Em testes, embrulhamos
 *    manualmente.
 */
import { AlertTriangle } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTransportMode } from "@/lib/realtime-store";

import {
  DEGRADED_BADGE_ARIA_LABEL,
  DEGRADED_BADGE_TEXT,
  DEGRADED_TOOLTIP_CONTENT,
} from "./DegradedModeBadge.constants";

const BADGE_BASE =
  "inline-flex items-center gap-1 rounded-full border px-2 sm:px-2.5 py-0.5 " +
  "text-xs font-semibold transition-colors focus:outline-none focus:ring-2 " +
  "focus:ring-ring focus:ring-offset-2";

const BADGE_WARNING =
  "border-yellow-300 bg-yellow-100 text-yellow-800 " +
  "dark:border-yellow-700/60 dark:bg-yellow-900/30 dark:text-yellow-200";

export function DegradedModeBadge(): JSX.Element | null {
  const mode = useTransportMode();
  if (mode !== "sse") return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label={DEGRADED_BADGE_ARIA_LABEL}
            tabIndex={0}
            className={cn(BADGE_BASE, BADGE_WARNING, "cursor-default")}
          >
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            <span>{DEGRADED_BADGE_TEXT}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs">
          {DEGRADED_TOOLTIP_CONTENT}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default DegradedModeBadge;
